import { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Select, Space, Table, Tag, Tooltip, Typography } from 'antd';
import { EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { ColumnsType } from 'antd/es/table';

import { usePageHeader } from 'src/hooks/misc';
import { listTemplateContextsAction } from '../../../../store/actions/templatecontext.action';
import {
  ENCOUNTER_PHASE_OPTIONS,
  type TemplateContextRecord,
} from './templateContext.constants';
import AddTemplateContext from './AddTemplateContext';
import EditTemplateContext from './EditTemplateContext';
import DeleteTemplateContext from './DeleteTemplateContext';

const TemplateContextContent = () => {
  const dispatch = useDispatch();

  const { templateContextList, templateContextLoading } = useSelector(
    (state: any) => state?.templateContextState
  );

  const [addModalOpen, setAddModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  const [editingRecord, setEditingRecord] =
    useState<TemplateContextRecord | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const [filters, setFilters] = useState<{
    encounter_phase?: string;
  }>({});

  usePageHeader({ title: 'Template Context' });

  // Fetch list on mount and when filters change
  useEffect(() => {
    const params: Record<string, string> = {};
    if (filters.encounter_phase)
      params.encounter_phase = filters.encounter_phase;
    dispatch(listTemplateContextsAction(params) as any);
  }, [dispatch, filters]);

  // Build filters object for passing to modals (so they can refetch)
  const activeFilters = useMemo(() => {
    const params: Record<string, string> = {};
    if (filters.encounter_phase)
      params.encounter_phase = filters.encounter_phase;
    return params;
  }, [filters]);

  // Handlers
  const handleEdit = (record: TemplateContextRecord) => {
    setEditingRecord(record);
    setEditModalOpen(true);
  };

  const handleDelete = (id: string) => {
    setDeletingId(id);
    setDeleteModalOpen(true);
  };

  // Table columns
  const columns: ColumnsType<TemplateContextRecord> = [
    {
      title: 'Template Name',
      dataIndex: 'template_name',
      key: 'template_name',
      sorter: (a, b) =>
        (a.template_name ?? '').localeCompare(b.template_name ?? ''),
      render: (value) => value || 'N/A',
    },
    {
      title: 'Variable Name',
      dataIndex: 'variable_name',
      key: 'variable_name',
      sorter: (a, b) => a.variable_name.localeCompare(b.variable_name),
    },
    {
      title: 'Section',
      dataIndex: 'section_name',
      key: 'section_name',
      render: (value) => value || 'N/A',
    },
    {
      title: 'Encounter Phase',
      dataIndex: 'encounter_phase',
      key: 'encounter_phase',
      render: (value) => (value ? <Tag color="blue">{value}</Tag> : 'N/A'),
    },
    {
      title: 'Matching Criteria',
      dataIndex: 'matching_criteria',
      key: 'matching_criteria',
      render: (value) => {
        if (!value || Object.keys(value).length === 0) return 'N/A';
        return (
          <Typography.Text ellipsis style={{ maxWidth: 200 }}>
            {JSON.stringify(value)}
          </Typography.Text>
        );
      },
    },
    {
      title: '',
      key: 'actions',
      width: 80,
      render: (_, record) => (
        <Space>
          <Tooltip title="Edit">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          <Tooltip title="Delete">
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              onClick={() => handleDelete(record.template_context_id)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div className="pt-4 pl-2">
      <div className="flex justify-between items-end">
        <div className="flex items-center gap-2">
          <label className="text-md text-gray-600 font-medium">
            Encounter Phase:
          </label>
          <Select
            allowClear
            style={{ minWidth: 200 }}
            placeholder="All phases"
            options={ENCOUNTER_PHASE_OPTIONS}
            value={filters.encounter_phase}
            onChange={(value) =>
              setFilters({ encounter_phase: value as string | undefined })
            }
          />
        </div>
        <Button type="primary" onClick={() => setAddModalOpen(true)}>
          Add Template Context
        </Button>
      </div>
      <div className="mt-5 ant-table rounded-xl">
        <Table
          bordered
          rowKey="template_context_id"
          loading={templateContextLoading}
          dataSource={templateContextList}
          columns={columns}
          pagination={{ pageSize: 10 }}
        />
      </div>

      <AddTemplateContext
        isOpen={addModalOpen}
        setIsOpen={setAddModalOpen}
        filters={activeFilters}
      />

      <EditTemplateContext
        isOpen={editModalOpen}
        setIsOpen={setEditModalOpen}
        record={editingRecord}
        filters={activeFilters}
      />

      <DeleteTemplateContext
        isOpen={deleteModalOpen}
        setIsOpen={setDeleteModalOpen}
        templateContextId={deletingId}
        filters={activeFilters}
      />
    </div>
  );
};

export default TemplateContextContent;
